import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Globe, BookOpen, ArrowLeft, Clock, Eye, Heart, Play, MessageCircle, Search as SearchIcon } from 'lucide-react';
import { storyApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface Story {
  id: string;
  title: string;
  coverUrl?: string;
  status: 'DRAFT' | 'REVIEWING' | 'COMPLETED' | 'APPROVED' | 'REJECTED' | 'PUBLISHED';
  createdAt: string;
  pageCount: number;
  isPublic?: boolean;
  theme?: string;
  tags?: string[];
  viewCount?: number;
  likeCount?: number;
  playCount?: number;
  commentCount?: number;
  user?: {
    id: string;
    username: string;
    avatarUrl?: string;
  };
}

const categoryFilters = [
  { value: 'all', label: '全部主题' },
  { value: 'adventure', label: '冒险' },
  { value: 'friendship', label: '友谊' },
  { value: 'space', label: '太空' },
  { value: 'dinosaur', label: '恐龙' },
  { value: 'nature', label: '自然' },
];

function PublicLibrary() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  // Workaround: TypeScript 5.9 在 react-jsx 模式下偶发无法识别 JSX 中的变量使用
  void SearchIcon;
  void setSearchQuery;
  const [activeCategory, setActiveCategory] = useState('all');
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    async function fetchStories() {
      try {
        setLoading(true);
        const res = await storyApi.getPublicStories({
          tags: activeCategory === 'all' ? undefined : activeCategory,
          page,
          pageSize,
        });
        const data = res.data?.data || {};
        setStories(data.list || []);
        setTotal(data.total || 0);
      } catch (err) {
        setError('获取公开绘本列表失败，请稍后重试');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchStories();
  }, [activeCategory, page]);

  const filteredStories = stories.filter((story) => {
    if (searchQuery && !story.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="w-full flex flex-col items-center py-8 lg:py-12">
      <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* 头部 */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              title="返回首页"
            >
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </button>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
              公开社区
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base">发现大家创作的精彩绘本 · 共 {total} 本</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/profile')}
            className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl font-medium transition-all px-4 py-2"
          >
            <BookOpen className="w-5 h-5" />
            我的绘本
          </button>
        </div>
      </div>

      {/* 搜索和筛选 */}
      <div className="flex flex-col gap-4 mb-8">
        {/* 搜索框 -->
        <div className="relative max-w-[400px]">
          <div className="flex items-center gap-2 h-12 bg-white border border-slate-200 rounded-xl overflow-hidden focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/20 transition-all px-3">
            <SearchIcon className="w-5 h-5 text-slate-400 flex-shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索公开绘本..."
              className="flex-1 bg-transparent text-slate-700 placeholder:text-slate-400 focus:outline-none"
            />
          </div>
        </div>

        {/* 主题分类筛选 */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <span className="text-sm text-slate-500 whitespace-nowrap">主题:</span>
          {categoryFilters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => { setActiveCategory(filter.value); setPage(1); }}
              className={`rounded-full text-sm font-medium whitespace-nowrap transition-all px-5 py-2.5 border ${
                activeCategory === filter.value
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border-slate-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* 内容区 */}
      <div className="min-h-[500px]">
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200">
              <div className="aspect-[4/3] bg-slate-200 animate-shimmer" />
              <div className="p-5 flex flex-col gap-2">
                <div className="h-5 bg-slate-200 rounded animate-shimmer w-3/4" />
                <div className="h-4 bg-slate-200 rounded animate-shimmer w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
          <div className="w-24 h-24 rounded-2xl bg-slate-100 flex items-center justify-center">
            <span className="text-4xl">⚠️</span>
          </div>
          <h3 className="text-xl font-bold text-slate-800">出错了</h3>
          <p className="text-slate-500">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl font-medium transition-all px-4 py-2"
          >
            重新加载
          </button>
        </div>
      ) : filteredStories.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStories.map((story, index) => (
              <motion.div
                key={story.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => {
                  if (!isAuthenticated) {
                    alert('请先注册登录后查看绘本');
                    navigate('/login');
                    return;
                  }
                  navigate(`/reader/${story.id}`);
                }}
              >
                {/* 封面 */}
                <div className="aspect-[4/3] bg-gradient-to-br from-indigo-100 to-violet-100 relative overflow-hidden">
                  {(story.coverUrl || (story as any).imageUrls?.[0]) ? (
                    <img 
                      src={story.coverUrl || (story as any).imageUrls?.[0]} 
                      alt={story.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <BookOpen className="w-16 h-16 text-indigo-200" />
                    </div>
                  )}
                </div>

                {/* 信息 */}
                <div className="p-5">
                  <h3 className="font-bold text-slate-800 mb-1 truncate">{story.title}</h3>
                  
                  {/* 作者 */}
                  {story.user && (
                    <div className="flex items-center gap-1.5 mb-2">
                      {story.user.avatarUrl ? (
                        <img
                          src={story.user.avatarUrl}
                          alt={story.user.username}
                          className="w-4 h-4 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-4 h-4 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] text-indigo-600 font-medium">
                          {(story.user.username || '匿')[0]}
                        </div>
                      )}
                      <span className="text-xs text-slate-500 truncate">{story.user.username || '匿名用户'}</span>
                    </div>
                  )}
                  
                  {story.tags && story.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {story.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-600 text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        {story.pageCount}页
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {new Date(story.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-0.5" title="阅读量">
                        <Eye className="w-4 h-4" />
                        {story.viewCount || 0}
                      </span>
                      <span className="flex items-center gap-0.5" title="播放量">
                        <Play className="w-4 h-4" />
                        {story.playCount || 0}
                      </span>
                      <span className="flex items-center gap-0.5" title="评论数">
                        <MessageCircle className="w-4 h-4" />
                        {story.commentCount || 0}
                      </span>
                      <span className="flex items-center gap-0.5" title="点赞量">
                        <Heart className="w-4 h-4" />
                        {story.likeCount || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* 分页 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <span className="text-sm text-slate-600">
                第 {page} / {totalPages} 页
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
          <div className="w-24 h-24 rounded-2xl bg-slate-100 flex items-center justify-center">
            <Globe className="w-12 h-12 text-slate-400" />
          </div>
          <h3 className="text-xl font-bold text-slate-800">暂无公开绘本</h3>
          <p className="text-slate-500">还没有公开绘本，去创作一本吧！</p>
          <button
            onClick={() => navigate('/create')}
            className="inline-flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl font-medium transition-all px-4 py-2"
          >
            开始创作
          </button>
        </div>
      )}
      </div>
      </div>
    </div>
  );
}

export default PublicLibrary;
